import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getActiveShops } from "./api";
import ShopNavBar from "./components/ShopNavBar";
import EmptyState from "./components/EmptyState";
import RenderTranslatedText from "../../components/RenderTranslatedText";

function ShopListSkeleton() {
  return (
    <div className="px-4 pt-4 flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 rounded-xl border border-gray-100"
        >
          <div className="w-14 h-14 rounded-lg bg-gray-100 animate-pulse shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-3.5 w-2/3 bg-gray-100 rounded animate-pulse" />
            <div className="h-3 w-1/3 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ActiveShops() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scannedShopId = sessionStorage.getItem("scannedShopId");

  useEffect(() => {
    getActiveShops({ limit: 50 })
      .then((res) => setShops(res.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pb-8">
      <ShopNavBar
        title={<RenderTranslatedText i8nKey="shop_shops_title" />}
        onBack={() => navigate("/shop/profile")}
      />

      {loading && <ShopListSkeleton />}

      {!loading && error && (
        <EmptyState title="Couldn't load shops" subtitle={error} />
      )}

      {!loading && !error && shops.length === 0 && (
        <EmptyState
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7h16l-1.5 11.2a2 2 0 01-2 1.8H7.5a2 2 0 01-2-1.8L4 7z"
                stroke="#9CA3AF"
                strokeWidth="1.6"
              />
              <path d="M8 7V5a4 4 0 018 0v2" stroke="#9CA3AF" strokeWidth="1.6" />
            </svg>
          }
          title="No active shops right now"
          subtitle="Scan a shop's QR code at their stall to get started."
        />
      )}

      {!loading && !error && shops.length > 0 && (
        <div className="px-4 pt-4 flex flex-col gap-3">
          {shops.map((shop) => {
            const unlocked = scannedShopId === shop.id;
            return (
              <div
                key={shop.id}
                onClick={() => unlocked && navigate(`/shop/${shop.id}`)}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  unlocked
                    ? "border-gray-100 cardShadow cursor-pointer bg-white active:scale-[0.98]"
                    : "border-gray-100 bg-gray-50"
                }`}
              >
                <div className="relative shrink-0">
                  <div
                    className={`w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center ${
                      unlocked ? "" : "grayscale opacity-60"
                    }`}
                  >
                    {shop.logoUrl ? (
                      <img
                        src={shop.logoUrl}
                        alt={shop.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="otaFont font-bold text-lg">
                        {shop.name?.[0]}
                      </span>
                    )}
                  </div>
                  {!unlocked && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <rect
                          x="5"
                          y="11"
                          width="14"
                          height="9"
                          rx="2"
                          stroke="#B54708"
                          strokeWidth="2"
                        />
                        <path
                          d="M8 11V8a4 4 0 018 0v3"
                          stroke="#B54708"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-semibold truncate ${unlocked ? "otaDark" : "text-gray-400"}`}
                  >
                    {shop.name}
                  </p>
                  {unlocked ? (
                    <p className="text-xs otaLight truncate">
                      {shop._count?.products ?? 0} products
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium">
                      <RenderTranslatedText i8nKey="shop_shops_locked_message" />
                    </p>
                  )}
                </div>
                {unlocked && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 18l6-6-6-6"
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
